import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { GroupedTabData } from './group-chart.modals';
import { ProcessingState } from '@services/core-services/models';

export interface PaginationInfo {
  limit: number;
  current_page: number;
  total_records: number;
  total_pages: number;
  next_page: number | null;
  previous_page: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class PaginatedDataService {
  constructor() {}

  /**
   * Updates pagination for a specific tab within grouped data
   *
   * @param allData The entire dataset indexed by tab ID
   * @param tabId The ID of the current tab to paginate
   * @param currentStats The current stats that need to be updated with new page data
   * @param page The page number to display
   * @param limit The number of items per page
   * @returns An object containing the updated pagination info and paginated stats
   */
  updatePagination(
    allData: GroupedTabData,
    tabId: string,
    currentStats: GroupedTabData,
    page: number,
    limit: number = 8
  ): { paginationInfo: PaginationInfo; updatedStats: GroupedTabData } {
    const allDataForTab = allData[tabId] || [];
    const totalRecords = allDataForTab.length;
    const totalPages = Math.ceil(totalRecords / limit);

    // Create pagination info
    const paginationInfo: PaginationInfo = {
      limit,
      current_page: page,
      total_records: totalRecords,
      total_pages: totalPages,
      next_page: page < totalPages ? page + 1 : null,
      previous_page: page > 1 ? page - 1 : null,
    };

    // Calculate data for current page
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = allDataForTab.slice(startIndex, endIndex);

    // Update stats with paginated data
    const updatedStats = { ...currentStats };
    updatedStats[tabId] = paginatedData;

    return { paginationInfo, updatedStats };
  }

  /**
   * Creates state observables for a paginated dataset
   *
   * @returns An object containing BehaviorSubjects for data, pagination, and loader state
   */
  createPaginatedState(): {
    data: BehaviorSubject<GroupedTabData>;
    allData: BehaviorSubject<GroupedTabData>;
    pagination: BehaviorSubject<PaginationInfo>;
    loader: BehaviorSubject<ProcessingState>;
  } {
    const defaultPagination: PaginationInfo = {
      limit: 8,
      current_page: 1,
      total_records: 0,
      total_pages: 1,
      next_page: null,
      previous_page: null,
    };

    return {
      data: new BehaviorSubject<GroupedTabData>({}),
      allData: new BehaviorSubject<GroupedTabData>({}),
      pagination: new BehaviorSubject<PaginationInfo>(defaultPagination),
      loader: new BehaviorSubject<ProcessingState>(ProcessingState.Loading),
    };
  }
}
